import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSearchOptionsFromApi } from '@/lib/api';
import { buildSearchPath } from '@/lib/seo';

const fallbackSpecialties = [
  'Médecin généraliste', 'Cardiologue', 'Dermatologue', 'Pédiatre', 'Gynécologue',
  'Dentiste', 'Ophtalmologue', 'ORL', 'Gastro-entérologue', 'Rhumatologue',
  'Neurologue', 'Psychiatre', 'Urologue', 'Endocrinologue', 'Orthopédiste',
  'Laboratoire d\'analyses', 'Radiologie', 'Pharmacie', 'Kinésithérapeute',
  'Orthophoniste', 'Ostéopathe', 'Infirmier à domicile',
];

const fallbackDistricts = [
  'Agdal', 'Hassan', 'Hay Riad', 'Souissi',
  'L\'Océan', 'Youssoufia', 'Yacoub El Mansour',
];

export default function Hero() {
  const navigate = useNavigate();
  const [city, setCity] = useState('Rabat');
  const [cities, setCities] = useState<string[]>(['Rabat']);
  const [specialty, setSpecialty] = useState('');
  const [district, setDistrict] = useState('');
  const [conventionCode, setConventionCode] = useState('');
  const [showCity, setShowCity] = useState(false);
  const [showSpec, setShowSpec] = useState(false);
  const [showDist, setShowDist] = useState(false);
  const [specialties, setSpecialties] = useState<string[]>(fallbackSpecialties);
  const [districts, setDistricts] = useState<string[]>(fallbackDistricts);

  useEffect(() => {
    let active = true;
    fetchSearchOptionsFromApi()
      .then((options) => {
        if (!active) return;
        setCities(options.cities.length > 0 ? options.cities : ['Rabat']);
        setSpecialties(options.specialties.length > 0 ? options.specialties : fallbackSpecialties);
        setDistricts(options.districts.length > 0 ? options.districts : fallbackDistricts);
        if (options.cities.length > 0) {
          setCity(options.cities[0]);
        }
      })
      .catch(() => {
        if (active) {
          setCities(['Rabat']);
          setSpecialties(fallbackSpecialties);
          setDistricts(fallbackDistricts);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const handleSearch = () => {
    if (district) {
      navigate(buildSearchPath(district, specialty));
      return;
    }
    const params = new URLSearchParams();
    if (specialty) params.set('specialty', specialty);
    const qs = params.toString();
    navigate(qs ? `/recherche?${qs}` : '/recherche');
  };

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Serene%20modern%20abstract%20medical%20wellness%20scene%20with%20soft%20mint%20green%20and%20warm%20cream%20tones%2C%20smooth%20organic%20curves%2C%20subtle%20stethoscope%20silhouette%2C%20minimal%20editorial%20photography%20style%2C%20soft%20natural%20lighting%2C%20clean%20airy%20composition%20evoking%20trust%20and%20care%2C%20muted%20sophisticated%20color%20palette%2C%20contemporary%20healthcare%20branding%20aesthetic%20with%20blurred%20botanical%20elements%20and%20warm%20highlights&width=1920&height=1200&seq=medicaclic-hero-01&orientation=landscape"
        alt="MEDICACLIC santé Rabat"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-secondary-900/70 via-secondary-800/55 to-primary-900/60" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-32">
        <div className="max-w-4xl mx-auto text-center animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary-300 animate-soft-pulse" />
            <span className="text-xs font-medium text-background-50 tracking-wide uppercase whitespace-nowrap">
              Disponible à Rabat
              <span className="hidden sm:inline"> · Bientôt Casablanca &amp; Tanger</span>
            </span>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-semibold text-background-50 leading-[1.05] mb-6">
            Votre santé,<br />
            <span className="italic text-primary-300">en quelques clics.</span>
          </h1>

          <p className="text-base md:text-lg text-background-100 max-w-2xl mx-auto mb-10 leading-relaxed">
            Trouvez un médecin spécialiste, un laboratoire d'analyses, un centre de radiologie
            ou une pharmacie près de chez vous. Rendez-vous confirmé instantanément
            sur l'agenda réel de votre praticien.
          </p>

          <div id="recherche" className="bg-background-50 rounded-2xl p-4 md:p-5 max-w-3xl mx-auto text-left border border-background-200">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-3 relative">
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Ville</label>
                <button
                  onClick={() => { setShowCity(!showCity); setShowSpec(false); setShowDist(false); }}
                  className="w-full flex items-center justify-between gap-2 px-3.5 py-3 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-800 cursor-pointer overflow-hidden"
                >
                  <span className={`truncate min-w-0 ${city ? '' : 'text-foreground-500'}`}>
                    {city || 'Choisir une ville'}
                  </span>
                  <i className="ri-map-pin-2-line flex-shrink-0 text-foreground-500 text-lg w-5 h-5 flex items-center justify-center" />
                </button>
                {showCity && (
                  <div className="absolute left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-md max-h-64 overflow-y-auto z-20">
                    {cities.map((c) => (
                      <button
                        key={c}
                        onClick={() => { setCity(c); setShowCity(false); }}
                        className="w-full text-left px-3.5 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 cursor-pointer"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-3 relative">
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Spécialité</label>
                <button
                  onClick={() => { setShowSpec(!showSpec); setShowCity(false); setShowDist(false); }}
                  className="w-full flex items-center justify-between gap-2 px-3.5 py-3 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-800 cursor-pointer overflow-hidden"
                >
                  <span className={`truncate min-w-0 ${specialty ? '' : 'text-foreground-500'}`}>
                    {specialty || 'Toutes les spécialités'}
                  </span>
                  <i className="ri-arrow-down-s-line flex-shrink-0 text-foreground-500 text-lg w-5 h-5 flex items-center justify-center" />
                </button>
                {showSpec && (
                  <div className="absolute left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-md max-h-64 overflow-y-auto z-20">
                    {specialties.map((s) => (
                      <button
                        key={s}
                        onClick={() => { setSpecialty(s); setShowSpec(false); }}
                        className="w-full text-left px-3.5 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 cursor-pointer"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-3 relative">
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Quartier</label>
                <button
                  onClick={() => { setShowDist(!showDist); setShowCity(false); setShowSpec(false); }}
                  className="w-full flex items-center justify-between gap-2 px-3.5 py-3 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-800 cursor-pointer overflow-hidden"
                >
                  <span className={`truncate min-w-0 ${district ? '' : 'text-foreground-500'}`}>
                    {district || 'Tous les quartiers'}
                  </span>
                  <i className="ri-map-pin-line flex-shrink-0 text-foreground-500 text-lg w-5 h-5 flex items-center justify-center" />
                </button>
                {showDist && (
                  <div className="absolute left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-md max-h-64 overflow-y-auto z-20">
                    {districts.map((d) => (
                      <button
                        key={d}
                        onClick={() => { setDistrict(d); setShowDist(false); }}
                        className="w-full text-left px-3.5 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 cursor-pointer"
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-3 relative">
                <label className="flex flex-wrap items-center gap-x-1 text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">
                  Code partenaire
                  <span className="text-foreground-400 font-normal normal-case">(facultatif)</span>
                </label>
                <input
                  type="text"
                  value={conventionCode}
                  onChange={(e) => setConventionCode(e.target.value.toUpperCase())}
                  placeholder="Ex. AC"
                  maxLength={6}
                  className="w-full px-3.5 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:outline-none text-sm text-foreground-800 uppercase tracking-wider"
                />
              </div>
            </div>

            <button
              onClick={handleSearch}
              className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-lg w-5 h-5 flex items-center justify-center" />
              Rechercher un praticien
            </button>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-background-200">
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-primary-300 w-4 h-4 flex items-center justify-center" />
              Gratuit pour les patients
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-primary-300 w-4 h-4 flex items-center justify-center" />
              Confirmation immédiate
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-lock-line text-primary-300 w-4 h-4 flex items-center justify-center" />
              Conforme Loi 09-08 · CNDP
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}